<template>
  <div>
    <create-study-set-overview
      :studysetDetails="studysetDetails"
      :organizationType="organizationType"
      :organizationId="organizationId"
      :uploadId="uploadId"
      :success="createdSuccess"
      @update:uploadId="$emit('update:uploadId', $event)"
    />
    <api-errors :errors="errors" />
    <study-set-create-footer
      v-if="!review"
      :cancelActive="true"
      :primaryPreviousActive="true"
      :primaryTextShowIcon="false"
      :previousShowIcon="true"
      :primaryAction="createReadAloudAndWarmUp"
      :primaryPreviousAction="editClass"
      :primaryCancelAction="cancelClass"
      :primaryText="$t('read_aloud.create_read_aloud')"
      :primaryPreviousText="$t('study_sets.create_studyset_flow.previous')"
    />
    <study-set-create-footer
      v-else
      :primaryTextShowIcon="false"
      :cancelActive="true"
      :previousShowIcon="false"
      :primaryPreviousActive="true"
      :primaryPreviousAction="reviewDone"
      :primaryAction="assignToClass"
      :primaryCancelAction="reviewDone"
      :primaryPreviousText="$t('common.done')"
      :primaryText="$t($i18nLabelsHelper(currentOrganizationType, `study_sets.assign_to_group`))"
    />
  </div>
</template>

<script>
import ApiErrors from "@/components/common/ApiErrors";
import CreateStudySetOverview from "./CreateReadAloudOverview";
import StudySetCreateFooter from "./ReadAloudCreateFooter";
import { always, isNil, path, dissoc, pathOr, length, compose, propOr, objOf, map } from "ramda";
import { mapActions, mapGetters, mapState } from "vuex";
import { successMessage, warningMessage } from "@/utils/messages";
import axios from "@/utils/axios";

export default {
  components: {
    ApiErrors,
    CreateStudySetOverview,
    StudySetCreateFooter,
  },
  props: {
    studysetDetails: {
      type: Object,
      default: always({ users: [] }),
    },
    currentStep: {
      type: Number,
      default: 2,
    },
    organizationType: {
      type: String,
      default: "",
    },
    uploadId: {
      default: null,
    },
    organizationId: {},
  },
  computed: {
    ...mapGetters({
      currentOrganizationType: "organization/currentOrganizationType",
    }),
    ...mapState("readAlouds", ["finalWarmUpStudySets"]),
  },
  data() {
    return {
      showStudentsModal: false,
      showImportModal: false,
      errors: {},
      uploadDetails: {},
      primaryLoading: false,
      review: false,
      createdSuccess: false,
      uploadImageUrl: "",
    };
  },
  methods: {
    ...mapActions({
      createReadAloudAction: "readAlouds/createReadAloud",
      updateReadAloudAction: "readAlouds/updateStudySet",
      updateBuildingStudySet: "readAlouds/updateBuildingStudySet",
      createWarmUpStudySets: "readAlouds/createWarmUpStudySets",
      setFinalWarmUpStudySets: "readAlouds/setFinalWarmUpStudySets",
      uploadImageS3: "readAlouds/uploadImageS3",
    }),
    openDisplayStudentsModal() {
      this.showStudentsModal = true;
    },
    openImportUsersModal() {
      this.showImportModal = true;
    },

    async createReadAloudAndWarmUp() {
      try {
        this.primaryLoading = this.$loading({
          lock: true,
          text: this.$t("common.loading"),
          spinner: "el-icon-loading",
          background: "rgba(0, 0, 0, 0.7)",
        });
        let response;

        const resUploadImage = await this.uploadImageS3();
        const imageURL = resUploadImage ? resUploadImage.callback_id : "";

        const phrases = this.finalWarmUpStudySets.map(item => item.text);

        response = await this.createWarmUpStudySets(phrases);
        const warmUpStudySetId = response.data.id;
        await this.createReadAloudAction({ warmUpStudySetId, imageUrl: imageURL });

        const acceptedPhrases = response.data.phrases;
        this.createdSuccess = true;
        if (phrases.length !== acceptedPhrases.length) {
          this.createdSuccess = false;
        }

        const tempPhrases = [...this.finalWarmUpStudySets];

        for (let i = 0; i < tempPhrases.length; i++) {
          if (acceptedPhrases[i] != tempPhrases[i].text) {
            tempPhrases[i].warning = true;
            tempPhrases[i].original_text = tempPhrases[i].text;
            tempPhrases[i].text = acceptedPhrases[i];
          } else {
            tempPhrases[i].success = true;
          }
        }

        this.setFinalWarmUpStudySets(tempPhrases);

        if (this.createdSuccess) {
          this.$emit("last-step");
          successMessage(
            this.$t("read_aloud.messages.your_read_aloud_has_been_updated_successfully"),
            "elsa-message success",
            {
              showClose: false,
            }
          )(this);
        }
        let x = {
          name: response.data.name,
          category: response.data.category,
          shareable: response.data.shareable,
          owner: response.data.owner,
          organization: response.data.organizationId,
          created: true,
          id: response.data.id,
          phrases: phrases,
        };

        this.updateBuildingStudySet(x);
        this.primaryLoading.close();
        this.review = true;
      } catch (error) {
        this.errors = error;
        this.primaryLoading.close();
      }
    },
    reviewDone() {
      return this.$emit("next-step", this.currentStep);
    },
    editClass() {
      this.$emit("edit-step");
    },
    cancelClass() {
      this.$emit("cancel");
    },
    assignToClass() {
      this.$emit("assign-to-class");
    },
    ...mapActions("invite", ["postSentInviteUploadEmails", "patchInviteUploadEmails", "getInviteUploadEmails"]),
  },
};
</script>

<style lang="scss" scoped>
@import "../../../scss/_colors.scss";

::v-deep .base-create-body {
  margin-top: 35px;
}

::v-deep .base-create-footer {
  margin-top: 24px;
}

::v-deep .generic-error-section {
  margin-bottom: 15px;
}
</style>
